import React, { useRef, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { Trans } from "react-i18next";
import { Briefcase, Lock, Mail, User } from "lucide-react";
import { t } from "../../i18n/i18next";
import type { AccountCreationSideEffects } from "../../redux/types/Authentication";
import { Turnstile, TurnstileHandle } from "../global/Turnstile";
import { VCard, VFontIcon } from "../ui";
import VSpinner from "../ui/spinners/VSpinner";
import { getApiPath, TURNSTILE_SITEKEY } from "../utilities/ApiUtils";
import { isDebug } from "../utilities/FeatureFlags";
import { apiCall } from "../utilities/StandaloneServerDataUtils";
import VU from "../utilities/ViridisUtils";
import LegalModal from "./LegalModal";

const TERMS_URL = "https://www.viridischem.com/evaluation-agreement/";

type State = {
    busy: boolean;
    email: string;
    password: string;
    confirmation: string;
    firstName: string;
    lastName: string;
    company: string;
    error: string;
    showTerms: boolean;
    acceptedTerms: boolean;
    turnstileToken?: string;
};

type Props = {
    callback: (email: string, result: AccountCreationSideEffects) => void;
};

const RegistrationForm: React.FC<Props> = ({ callback }) => {
    const turnstile = useRef<TurnstileHandle>(null);
    const [state, setState] = useState<State>({
        busy: false,
        email: "",
        password: "",
        confirmation: "",
        firstName: "",
        lastName: "",
        company: "",
        error: "",
        showTerms: false,
        acceptedTerms: false,
    });

    const set = <K extends keyof State>(key: K, value: State[K]) =>
        setState((current) => ({ ...current, [key]: value }));
    const resetVerification = () => {
        turnstile.current?.reset();
        set("turnstileToken", undefined);
    };
    const reject = (message: string) => {
        set("error", t(message));
        resetVerification();
    };

    const submit = () => {
        if (!state.email) return reject("Email address is required.");
        if (!VU.validateEmail(state.email))
            return reject("Please enter a valid email address.");
        if (!state.password) return reject("Please enter a password.");
        if (state.password !== state.confirmation)
            return reject("Password fields do not match.");
        if (!state.firstName || !state.lastName)
            return reject("Please enter your name.");
        if (!state.company)
            return reject(
                "Please enter your company name. If you are an individual, just enter your name."
            );
        if (!state.acceptedTerms)
            return reject(
                "You must accept the Evaluation Agreement to use the ViridisChem product."
            );
        if (!isDebug() && !state.turnstileToken)
            return reject("We couldn't verify your request. Please try again.");

        set("busy", true);
        apiCall(
            getApiPath("register"),
            {
                email: state.email,
                password: state.password,
                first_name: state.firstName,
                last_name: state.lastName,
                company: state.company,
                trial: true,
                "cf-turnstile-response": state.turnstileToken,
            },
            {
                success: (response?: AccountCreationSideEffects) => {
                    set("busy", false);
                    callback(state.email, response ?? {});
                },
                error: (error: string) => {
                    resetVerification();
                    setState((current) => ({
                        ...current,
                        error,
                        busy: false,
                    }));
                },
            }
        );
    };

    return (
        <div>
            <LegalModal
                show={state.showTerms}
                title={t("Evaluation Agreement")}
                mode="iframe"
                url={TERMS_URL}
                onClose={(accepted) =>
                    setState((current) => ({
                        ...current,
                        showTerms: false,
                        acceptedTerms: accepted,
                    }))
                }
            />
            {!VU.getParameterByName("accountLink") && (
                <div>{t("registration_intro")}</div>
            )}
            <br />
            <VCard variant="well">
                <Field
                    id="email"
                    label="Email address"
                    icon={<VFontIcon icon={Mail} />}
                    type="email"
                    value={state.email}
                    onChange={(value) => set("email", value)}
                />
                <Row>
                    <Col xs={6}>
                        <Field
                            id="password"
                            label="Password"
                            icon={<VFontIcon icon={Lock} />}
                            type="password"
                            value={state.password}
                            onChange={(value) => set("password", value)}
                        />
                    </Col>
                    <Col xs={6}>
                        <Field
                            id="password2"
                            label="Re-enter password"
                            icon={<VFontIcon icon={Lock} />}
                            type="password"
                            value={state.confirmation}
                            onChange={(value) => set("confirmation", value)}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col xs={6}>
                        <Field
                            id="first_name"
                            label="First name"
                            icon={<VFontIcon icon={User} />}
                            value={state.firstName}
                            onChange={(value) => set("firstName", value)}
                        />
                    </Col>
                    <Col xs={6}>
                        <Field
                            id="last_name"
                            label="Last name"
                            icon={<VFontIcon icon={User} />}
                            value={state.lastName}
                            onChange={(value) => set("lastName", value)}
                        />
                    </Col>
                </Row>
                <Field
                    id="company"
                    label="Company/Organization name"
                    icon={<VFontIcon icon={Briefcase} />}
                    value={state.company}
                    onChange={(value) => set("company", value)}
                />
                <Turnstile
                    ref={turnstile}
                    sitekey={TURNSTILE_SITEKEY}
                    onExpire={() => set("turnstileToken", undefined)}
                    onError={() => set("turnstileToken", undefined)}
                    onVerify={(value) => set("turnstileToken", value)}
                />
                <Button
                    variant="primary"
                    size="lg"
                    onClick={submit}
                    disabled={state.busy}
                >
                    {state.busy ? <VSpinner /> : t("Create account")}
                </Button>
                <Form.Check
                    checked={state.acceptedTerms}
                    onChange={(event) =>
                        set("acceptedTerms", event.target.checked)
                    }
                />{" "}
                <Trans i18nKey="registration_toc_required">
                    I agree to the terms of the{" "}
                    <a
                        href="#"
                        onClick={(event) => {
                            event.preventDefault();
                            set("showTerms", true);
                            resetVerification();
                        }}
                    >
                        Evaluation Agreement
                    </a>
                    .
                </Trans>
                <br />
                <span style={{ color: "red" }}>{state.error}</span>
            </VCard>
        </div>
    );
};

type FieldProps = {
    id: string;
    label: string;
    value: string;
    icon: React.ReactNode;
    type?: string;
    onChange: (value: string) => void;
};

const Field: React.FC<FieldProps> = ({
    id,
    label,
    value,
    icon,
    type = "text",
    onChange,
}) => (
    <Form.Group>
        <Form.Label htmlFor={id}>
            {icon} {t(label)}
        </Form.Label>
        <Form.Control
            id={id}
            type={type}
            value={value}
            maxLength={255}
            placeholder={t(`Enter ${label.toLowerCase()}`)}
            onChange={(event) => onChange(event.target.value)}
        />
    </Form.Group>
);

export default RegistrationForm;
